/**
 * DemoPlanningRadar - Shows upcoming delivery scheduling (OPTIONAL)
 * 
 * Rule: Include ONLY if page doesn't feel long. If in doubt, drop it.
 * 
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { useState, useMemo } from "react";
import { Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { getDemoPlanningOrders, type DemoPlanningGroup } from "@/lib/demoData";
import { useLanguage } from "@/contexts/LanguageContext";

function DateGroup({ group, isExpanded, onToggle }: { 
  group: DemoPlanningGroup; 
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      {/* Header - no urgency colors */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">
            {group.dateLabel}
          </span>
        </div>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
          {group.orders.length}
        </span>
      </button>
      
      {/* Orders (when expanded) */}
      {isExpanded && (
        <div className="px-3 pb-2 space-y-1.5">
          {group.orders.map((order, i) => (
            <div 
              key={i}
              className="flex items-center justify-between bg-muted/30 rounded px-2.5 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-foreground truncate">
                  {order.customer}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {order.product}
                </p>
              </div>
              <span className="text-xs font-medium text-foreground ml-2">
                {order.price}₾
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DemoPlanningRadar() {
  const { t } = useLanguage();
  const planningOrders = useMemo(() => getDemoPlanningOrders(t), [t]);
  
  // Tomorrow expanded by default
  const [expandedDates, setExpandedDates] = useState<Set<string>>(
    new Set([planningOrders[0]?.date])
  );
  
  const toggleDate = (date: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };
  
  const totalOrders = planningOrders.reduce((sum, g) => sum + g.orders.length, 0);

  return (
    <div className="bg-background border border-border/60 rounded-lg overflow-hidden w-full max-w-md">
      {/* Header - subtle, no urgency */}
      <div className="flex items-center gap-2 p-3 border-b border-border/30 bg-muted/20">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">{t("landing.demo.upcoming")}</span>
        <span className="ml-auto text-xs text-muted-foreground/70 bg-muted/50 px-2 py-0.5 rounded-full">
          {totalOrders}
        </span>
      </div>
      
      {/* Date Groups */}
      <div className="divide-y divide-border/20">
        {planningOrders.map(group => (
          <DateGroup 
            key={group.date}
            group={group}
            isExpanded={expandedDates.has(group.date)}
            onToggle={() => toggleDate(group.date)}
          />
        ))}
      </div>
    </div>
  );
}